import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTickets, useCreateTicket, useCompanies } from '@/lib/adminApi';
import type {
  TicketSummary,
  TicketStatus,
  TicketSource,
  TicketPriority,
  TicketCategory,
} from '@ventin/shared';
import {
  PriorityBadge,
  CategoryBadge,
  PRIORITY_OPTIONS,
  CATEGORY_OPTIONS,
} from '@/components/tickets/ticketMeta';

/**
 * TicketsPage — /admin/tickets (TKT-03, TKT-04, TKT-06, TKT-07 admin side).
 *
 * Lists all tickets with status + source filters. ADMIN users see a Company
 * column. "New ticket" button opens an inline create dialog.
 */
export default function TicketsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<TicketSource | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<TicketCategory | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const filter = useMemo(() => {
    const f: Record<string, unknown> = {};
    if (statusFilter) f.status = statusFilter;
    if (sourceFilter) f.source = sourceFilter;
    if (priorityFilter) f.priority = priorityFilter;
    if (categoryFilter) f.category = categoryFilter;
    return f;
  }, [statusFilter, sourceFilter, priorityFilter, categoryFilter]);

  const { data, isLoading, isError } = useTickets(filter);
  const tickets = useMemo(() => data?.tickets ?? [], [data]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold leading-[1.3]">Tickets</h2>
          <p className="text-sm text-muted-foreground mt-1">View and manage support tickets.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="sr-only">
              Status
            </label>
            <select
              id="status-filter"
              aria-label="Status"
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TicketStatus | '')}
            >
              <option value="">All statuses</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="WAITING_CLIENT">Waiting Client</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="source-filter" className="sr-only">
              Source
            </label>
            <select
              id="source-filter"
              aria-label="Source"
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as TicketSource | '')}
            >
              <option value="">All sources</option>
              <option value="PORTAL">Portal</option>
              <option value="EMAIL">Email</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="priority-filter" className="sr-only">
              Priority
            </label>
            <select
              id="priority-filter"
              aria-label="Priority"
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as TicketPriority | '')}
            >
              <option value="">All priorities</option>
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="category-filter" className="sr-only">
              Category
            </label>
            <select
              id="category-filter"
              aria-label="Category"
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as TicketCategory | '')}
            >
              <option value="">All categories</option>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={() => setDialogOpen(true)}>New ticket</Button>
        </div>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading tickets…</p>}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load tickets — try again.</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Billable</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No tickets found.
                  </td>
                </tr>
              )}
              {tickets.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="px-4 py-3 font-medium">{t.title}</td>
                  <td className="px-4 py-3">
                    <TicketStatusChip status={t.status} />
                  </td>
                  <td className="px-4 py-3">
                    <PriorityBadge priority={t.priority} />
                  </td>
                  <td className="px-4 py-3">
                    <CategoryBadge category={t.category} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{t.source}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.isBillable ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(t.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/admin/tickets/${t.id}`)}
                      aria-label={`Open ${t.title}`}
                    >
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TicketFormDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TicketStatusChip
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<TicketSummary['status'], { bg: string; dot: string; label: string }> = {
  OPEN: { bg: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500', label: 'Open' },
  IN_PROGRESS: { bg: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-500', label: 'In Progress' },
  WAITING_CLIENT: { bg: 'bg-orange-50 text-orange-700', dot: 'bg-orange-400', label: 'Waiting' },
  RESOLVED: { bg: 'bg-green-50 text-green-700', dot: 'bg-green-500', label: 'Resolved' },
  CLOSED: { bg: 'bg-[#F1F1F3] text-[#4B4B52]', dot: 'bg-[#8A8A94]', label: 'Closed' },
};

function TicketStatusChip({ status }: { status: TicketSummary['status'] }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        s.bg,
      )}
    >
      <span aria-hidden="true" className={cn('inline-block h-[6px] w-[6px] rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// TicketFormDialog — inline create dialog
// ---------------------------------------------------------------------------

interface TicketFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function TicketFormDialog({ open, onOpenChange }: TicketFormDialogProps) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('NORMAL');
  const [category, setCategory] = useState<TicketCategory>('CONTENT_UPDATE');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const createMut = useCreateTicket();
  const { data: companiesData } = useCompanies();
  const companies = companiesData?.companies ?? [];

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (title.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const input: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim(),
        priority,
        category,
      };
      if (companyId) input.companyId = companyId;
      const res = await createMut.mutateAsync(input as Parameters<typeof createMut.mutateAsync>[0]);
      toast.success('Ticket created');
      onOpenChange(false);
      setTitle('');
      setDescription('');
      setCompanyId('');
      setPriority('NORMAL');
      setCategory('CONTENT_UPDATE');
      navigate(`/admin/tickets/${res.ticket.id}`);
    } catch {
      toast.error("Couldn't create ticket — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New ticket</DialogTitle>
          <DialogDescription>
            Create a new support ticket. You will be taken to the detail page on submit.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ticket-title">Title</Label>
            <Input
              id="ticket-title"
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ticket-description">Description</Label>
            <textarea
              id="ticket-description"
              required
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ticket-priority-new">Priority</Label>
              <select
                id="ticket-priority-new"
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticket-category-new">Category</Label>
              <select
                id="ticket-category-new"
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={category}
                onChange={(e) => setCategory(e.target.value as TicketCategory)}
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {companies.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="ticket-company">Company (optional)</Label>
              <select
                id="ticket-company"
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">No company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
